import React, { useState, useEffect } from 'react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { FileText, Filter, Edit } from 'lucide-react';
import StatusChip from '../components/StatusChip';
import EditLicenseDialog from '../components/EditLicenseDialog';
import { api } from '../utils/api';
import { formatDate, getDaysUntil } from '../utils/helpers';

const LicenseRegister = ({ user }) => {
  const [licenses, setLicenses] = useState([]);
  const [filteredLicenses, setFilteredLicenses] = useState([]);
  const [stateFilter, setStateFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [loading, setLoading] = useState(true);
  const [editingLicense, setEditingLicense] = useState(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);

  useEffect(() => {
    loadLicenses();
    
    // Refresh data when window/tab regains focus
    const handleFocus = () => {
      loadLicenses();
    };
    
    window.addEventListener('focus', handleFocus);
    
    return () => {
      window.removeEventListener('focus', handleFocus);
    };
  }, []);

  useEffect(() => {
    applyFilters();
  }, [stateFilter, statusFilter, licenses]);

  const loadLicenses = async () => {
    try {
      // SINGLE SOURCE OF TRUTH: Load from state-compliance endpoint
      const complianceData = await api.getStateCompliance();
      
      // Extract all licenses from all states
      const allLicenses = [];
      Object.entries(complianceData.states || {}).forEach(([stateCode, stateData]) => {
        const stateLicenses = stateData.licenses || [];
        allLicenses.push(...stateLicenses);
      });
      
      // Sort by state and expiration date
      allLicenses.sort((a, b) => {
        if (a.state !== b.state) return a.state.localeCompare(b.state);
        return new Date(a.expiration_date) - new Date(b.expiration_date);
      });
      
      setLicenses(allLicenses);
      setFilteredLicenses(allLicenses);
    } catch (error) {
      console.error('Failed to load licenses:', error);
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = () => {
    let filtered = [...licenses];
    if (stateFilter !== 'all') {
      filtered = filtered.filter(l => l.state === stateFilter);
    }
    if (statusFilter !== 'all') {
      filtered = filtered.filter(l => l.status === statusFilter);
    }
    setFilteredLicenses(filtered);
  };

  const handleEdit = (license) => {
    setEditingLicense(license);
    setEditDialogOpen(true);
  };

  const handleEditSuccess = () => {
    loadLicenses();
  };

  const states = [...new Set(licenses.map(l => l.state))].sort();

  if (loading) {
    return <div className="flex justify-center py-12"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div></div>;
  }

  return (
    <div className="space-y-6 fade-in max-w-full overflow-hidden" data-testid="license-register-container">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div className="min-w-0 flex-1">
          <h1 className="text-2xl sm:text-3xl md:text-4xl font-semibold tracking-tight">License Register</h1>
          <p className="text-muted-foreground mt-2">Insurance agency and producer licenses across all states</p>
        </div>
      </div>

      {/* Filter Bar */}
      <Card data-testid="filter-bar">
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
            <Filter className="h-4 w-4 text-muted-foreground flex-shrink-0" />
            <Select value={stateFilter} onValueChange={setStateFilter} data-testid="filter-state-select">
              <SelectTrigger className="w-full sm:w-48">
                <SelectValue placeholder="Filter by state" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All States</SelectItem>
                {states.map(state => (
                  <SelectItem key={state} value={state}>{state}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={statusFilter} onValueChange={setStatusFilter} data-testid="filter-status-select">
              <SelectTrigger className="w-full sm:w-48">
                <SelectValue placeholder="Filter by status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Statuses</SelectItem>
                <SelectItem value="active">Active</SelectItem>
                <SelectItem value="pending_renewal">Pending Renewal</SelectItem>
                <SelectItem value="expired">Expired</SelectItem>
              </SelectContent>
            </Select>
            <div className="text-sm text-muted-foreground sm:ml-auto w-full sm:w-auto">
              Showing {filteredLicenses.length} of {licenses.length} licenses
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Licenses Table */}
      <Card>
        <CardContent className="p-0">
          <div className="overflow-x-auto -mx-0" data-testid="licenses-table">
            <table className="w-full min-w-[900px]">
              <thead className="bg-muted/50">
                <tr className="border-b border-border">
                  <th className="text-left p-3 text-xs sm:text-sm font-medium text-muted-foreground">License ID</th>
                  <th className="text-left p-3 text-xs sm:text-sm font-medium text-muted-foreground">Type</th>
                  <th className="text-left p-3 text-xs sm:text-sm font-medium text-muted-foreground">State</th>
                  <th className="text-left p-3 text-xs sm:text-sm font-medium text-muted-foreground">License #</th>
                  <th className="text-left p-3 text-xs sm:text-sm font-medium text-muted-foreground">Expiration</th>
                  <th className="text-left p-3 text-xs sm:text-sm font-medium text-muted-foreground">Days Until</th>
                  <th className="text-left p-3 text-xs sm:text-sm font-medium text-muted-foreground">Status</th>
                  <th className="text-left p-3 text-xs sm:text-sm font-medium text-muted-foreground">Owner</th>
                  <th className="text-left p-3 text-xs sm:text-sm font-medium text-muted-foreground">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredLicenses.map((license) => {
                  const daysUntil = getDaysUntil(license.expiration_date);
                  return (
                    <tr key={license.license_id} className="border-b border-border hover:bg-muted/30 transition-colors" data-testid={`license-row-${license.license_id}`}>
                      <td className="p-3 text-xs font-mono text-muted-foreground">{license.license_id}</td>
                      <td className="p-3 text-sm">{license.license_type}</td>
                      <td className="p-3 text-sm font-medium">{license.state}</td>
                      <td className="p-3 text-xs font-mono">{license.license_number || 'N/A'}</td>
                      <td className="p-3 text-sm tabular-nums">{formatDate(license.expiration_date)}</td>
                      <td className="p-3 text-sm">
                        <span className={`font-medium ${
                          daysUntil < 0 ? 'text-rose-600' : 
                          daysUntil <= 30 ? 'text-amber-600' : 
                          'text-muted-foreground'
                        }`}>
                          {daysUntil < 0 ? `${Math.abs(daysUntil)} days ago` : `${daysUntil} days`}
                        </span>
                      </td>
                      <td className="p-3">
                        <StatusChip status={license.status} />
                      </td>
                      <td className="p-3 text-xs text-muted-foreground">{license.owner}</td>
                      <td className="p-3">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleEdit(license)}
                          data-testid={`edit-license-${license.license_id}`}
                        >
                          <Edit className="h-4 w-4" />
                        </Button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          {filteredLicenses.length === 0 && (
            <div className="text-center py-12 text-muted-foreground">
              No licenses match the current filters
            </div>
          )}
        </CardContent>
      </Card>

      <EditLicenseDialog
        license={editingLicense}
        open={editDialogOpen}
        onClose={() => setEditDialogOpen(false)}
        onSuccess={handleEditSuccess}
      />
    </div>
  );
};

export default LicenseRegister;
